// Third-party
import { styled } from '@mui/system';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Hooks
import { CircularProgress, Stack, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TCustomTheme } from '@mgm/common/theme';
import { formatDecimalHours } from '@mgm/common/utils';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import get from 'lodash/get';
import { useAppSelector } from '../../../../hooks/useAppStore';

// Redux-toolkit
import { getSelectedMember } from '../../../../store/sheets/selectors';
import { getCurrencyById } from '../../../../store/common/selectors';

// Assets

const RootStyle = styled(Card)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2, 2, 3),
}));

const TimesheetsMemberTotal = () => {
  const selectedMember = useAppSelector(getSelectedMember);
  const getCurrency = useAppSelector(getCurrencyById);
  const { t } = useTranslation('timesheets');
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const totalItems = useMemo(() => {
    const currencyMark = getCurrency(selectedMember?.personnel?.paymentCurrencyId).mark;

    const totalAmount = parseFloat(selectedMember.totals?.totalPay);

    const totalPay = (totalAmount || 0)?.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const rawHours = get(selectedMember, 'totals.sheets.totalHours', '0');

    return [
      {
        title: t('totalHours'),
        value: `${formatDecimalHours(rawHours)}`,
        icon: 'tabler:clock-plus',
        color: '#FFAB00',
        sizes: {
          height: isMobile ? 45 : 80,
          width: isMobile ? 45 : 80,
        },
      },
      {
        title: t('totalAmount'),
        value: `${currencyMark}${totalPay}`,
        icon: 'ph:coins-light',
        color: '#00B8D9',
        sizes: {
          height: isMobile ? 50 : 80,
          width: isMobile ? 50 : 80,
        },
      },
    ];
  }, [selectedMember.totals, isMobile]);

  return (
    <>
      {totalItems.map((item) => (
        <Grid
          key={item.value}
          size={{
            xs: 12, sm: 12, md: 3.5, lg: 3.5,
          }}
        >
          <RootStyle>
            <div>
              <Typography variant="h4">{item.value}</Typography>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                {item.title}
              </Typography>
            </div>
            <Stack alignItems="center" justifyContent="center" sx={{ position: 'relative' }}>
              <Iconify
                icon={item.icon}
                width={32}
                sx={{ color: item.color, position: 'absolute' }}
              />

              <CircularProgress
                variant="determinate"
                value={100}
                size={62}
                thickness={4}
                sx={{ color: item.color, opacity: 0.48 }}
              />

              <CircularProgress
                variant="determinate"
                value={100}
                size={62}
                thickness={4}
                sx={{
                  top: 0,
                  left: 0,
                  opacity: 0.48,
                  position: 'absolute',
                  color: (theme) => alpha(theme.palette.grey[500], 0.16),
                }}
              />
            </Stack>
          </RootStyle>
        </Grid>
      ))}
    </>
  );
};

export {
  TimesheetsMemberTotal,
};
